import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Gauge,
  BedDouble,
  Droplets,
  Sparkles,
  ArrowRight,
  Activity,
} from "lucide-react";
import { setPageMeta, SITE_URL } from "@/lib/pageMeta";
import { SYMPTOMS, OCCUPATIONS, toolPath } from "@/lib/seoData";
import SmartwatchCTA from "@/components/silentscan/SmartwatchCTA";
import ComplianceFooter from "@/components/silentscan/ComplianceFooter";
import { useChatbot } from "@/context/ChatbotContext";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// ---- calculation logic (pure frontend) ----
// ADAG-derived estimated average glucose → eA1C conversion.
export const estimateA1C = (glucoseMgDl) => +(((glucoseMgDl + 46.7) / 28.7).toFixed(1));
export const a1cCategory = (a1c) => {
  if (a1c < 5.7) return { label: "Baseline range", color: "#34d399", note: "This estimate appears to sit within the standard baseline range." };
  if (a1c < 6.5) return { label: "Elevated baseline", color: "#fbbf24", note: "This estimate appears modestly elevated — a pattern often consistent with lifestyle-related glucose variability." };
  return { label: "High baseline", color: "#fb7185", note: "This estimate appears high. It may be worth discussing with a qualified professional." };
};

export const weeklySleepDebt = (hoursSlept) => {
  const nightly = Math.max(0, 8 - hoursSlept);
  return { nightly: +nightly.toFixed(1), weekly: +(nightly * 7).toFixed(1) };
};

export const hydrationTarget = (weightKg) => {
  const liters = +(weightKg * 0.033).toFixed(1);
  return { liters, glasses: Math.round((liters * 1000) / 250) };
};

export default function WellnessTools() {
  const { openChatbot } = useChatbot();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta({
      title: "Free Wellness Tools — A1C, Sleep Debt & Hydration Calculators | SilentScan AI",
      description:
        "Free instant wellness calculators: estimate A1C from average glucose, track your weekly sleep debt, and calculate daily hydration needs. No login required.",
    });
  }, []);

  return (
    <div data-testid="wellness-tools-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <Link
          to="/"
          data-testid="tools-back-home"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to home
        </Link>

        <motion.div variants={container} initial="hidden" animate="show" className="mt-8">
          <motion.div variants={item}>
            <div className="text-[11px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">
              Free Wellness Tools
            </div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight">
              Instant answers.
              <span className="text-zinc-500"> No sign-up needed.</span>
            </h1>
            <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-2xl leading-relaxed">
              Three micro-calculators that run entirely in your browser. Nothing you
              type leaves your device — educational estimates only, never medical
              advice.
            </p>
          </motion.div>

          <div className="mt-10 grid lg:grid-cols-3 gap-5">
            <motion.div variants={item}>
              <A1CCard />
            </motion.div>
            <motion.div variants={item}>
              <SleepCard />
            </motion.div>
            <motion.div variants={item}>
              <HydrationCard />
            </motion.div>
          </div>

          {/* Deeper funnel */}
          <motion.div
            variants={item}
            className="mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shrink-0 shadow-[0_0_25px_rgba(0,229,255,0.3)]">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-semibold">
                Want your full picture, not just one metric?
              </h2>
              <p className="mt-1 text-sm text-zinc-400 leading-relaxed">
                Take the free 16-question AI lifestyle check-in and get a
                personalized wellness score with explainable insights — still no
                login required.
              </p>
            </div>
            <button
              data-testid="tools-start-assessment-btn"
              onClick={openChatbot}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-glow text-white text-sm font-medium shrink-0"
            >
              Start free check-in
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          {/* Programmatic index — internal linking for crawlers + users */}
          <motion.div variants={item} className="mt-12" data-testid="tools-guides-index">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
              <Activity className="w-3.5 h-3.5" />
              Guided screening pages
            </div>
            <h2 className="mt-2 font-display text-2xl font-semibold">
              Lifestyle screening for your exact situation
            </h2>
            <p className="mt-1 text-sm text-zinc-500">
              Pick what you're feeling and what you do — each guide includes a free
              interactive screening.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SYMPTOMS.flatMap((s) =>
                OCCUPATIONS.map((o) => (
                  <Link
                    key={`${s.slug}-${o.slug}`}
                    to={toolPath(s, o)}
                    data-testid={`guide-link-${s.slug}-${o.slug}`}
                    className="text-left px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-400/30 hover:bg-white/[0.04] transition-colors text-xs text-zinc-300 hover:text-white"
                  >
                    How to fix {s.name.toLowerCase()} for {o.name.toLowerCase()}
                  </Link>
                ))
              )}
            </div>
          </motion.div>

          <ComplianceFooter variant="issue" />
        </motion.div>
      </div>
    </div>
  );
}

const CardShell = ({ icon: Icon, title, desc, testid, children }) => (
  <div
    data-testid={testid}
    className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 h-full flex flex-col"
  >
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#0B3D91]/40 to-cyan-500/15 border border-cyan-500/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-cyan-300" />
      </div>
      <h3 className="font-display text-lg font-semibold leading-tight">{title}</h3>
    </div>
    <p className="mt-2 text-xs text-zinc-500 leading-relaxed">{desc}</p>
    <div className="mt-5 flex-1 flex flex-col">{children}</div>
  </div>
);

const A1CCard = () => {
  const [glucose, setGlucose] = useState("140");
  const g = Number(glucose);
  const valid = glucose !== "" && !Number.isNaN(g) && g >= 40 && g <= 600;
  const a1c = useMemo(() => (valid ? estimateA1C(g) : null), [valid, g]);
  const cat = a1c != null ? a1cCategory(a1c) : null;

  return (
    <CardShell
      icon={Gauge}
      title="A1C Estimation Assistant"
      desc="Convert an average blood glucose reading (mg/dL) into an estimated A1C-style metric using the standard eAG formula."
      testid="calc-a1c"
    >
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Average blood glucose (mg/dL)
      </label>
      <input
        data-testid="a1c-glucose-input"
        type="number"
        min="40"
        max="600"
        value={glucose}
        onChange={(e) => setGlucose(e.target.value)}
        className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 focus:border-cyan-400/50 outline-none px-4 py-3 text-sm text-white"
        placeholder="e.g., 140"
      />
      {a1c != null && cat && (
        <div data-testid="a1c-result" className="mt-4 rounded-2xl bg-black/40 border border-white/10 p-4">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold" style={{ color: cat.color }}>
              {a1c}%
            </span>
            <span className="text-[11px] uppercase tracking-widest" style={{ color: cat.color }}>
              {cat.label}
            </span>
          </div>
          <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{cat.note}</p>
        </div>
      )}
      <SmartwatchCTA variant="track" source="calc-a1c" />
    </CardShell>
  );
};

const SleepCard = () => {
  const [hours, setHours] = useState(6);
  const debt = weeklySleepDebt(hours);
  const tone =
    debt.weekly === 0 ? "#34d399" : debt.weekly <= 7 ? "#fbbf24" : "#fb7185";

  return (
    <CardShell
      icon={BedDouble}
      title="Deep Sleep Deficit Tracker"
      desc="Compare last night's sleep against the 8-hour benchmark and see your active weekly sleep debt."
      testid="calc-sleep"
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        <span>Hours slept last night</span>
        <span className="text-cyan-300 text-sm normal-case tracking-normal font-semibold">
          {hours}h
        </span>
      </div>
      <input
        data-testid="sleep-hours-slider"
        type="range"
        min="3"
        max="10"
        step="0.5"
        value={hours}
        onChange={(e) => setHours(Number(e.target.value))}
        className="w-full mt-3 accent-cyan-400"
      />
      <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
        <span>3h</span>
        <span>8h benchmark</span>
        <span>10h</span>
      </div>
      <div data-testid="sleep-result" className="mt-4 rounded-2xl bg-black/40 border border-white/10 p-4">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl font-semibold" style={{ color: tone }}>
            {debt.weekly}h
          </span>
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">
            weekly sleep debt
          </span>
        </div>
        <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
          {debt.nightly === 0
            ? "You met the 8-hour benchmark — your routine appears to reflect no active deficit today."
            : `You're running ${debt.nightly}h short per night, which compounds to roughly ${debt.weekly}h across a week.`}
        </p>
      </div>
      <SmartwatchCTA variant="track" source="calc-sleep" />
    </CardShell>
  );
};

const HydrationCard = () => {
  const [weight, setWeight] = useState("65");
  const w = Number(weight);
  const valid = weight !== "" && !Number.isNaN(w) && w >= 20 && w <= 300;
  const target = useMemo(() => (valid ? hydrationTarget(w) : null), [valid, w]);

  return (
    <CardShell
      icon={Droplets}
      title="Daily Hydration Matrix"
      desc="Enter your body weight to get a recommended daily water intake, in litres and glasses."
      testid="calc-hydration"
    >
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        Body weight (kg)
      </label>
      <input
        data-testid="hydration-weight-input"
        type="number"
        min="20"
        max="300"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        className="mt-2 w-full rounded-xl bg-black/40 border border-white/10 focus:border-cyan-400/50 outline-none px-4 py-3 text-sm text-white"
        placeholder="e.g., 65"
      />
      {target && (
        <div data-testid="hydration-result" className="mt-4 rounded-2xl bg-black/40 border border-white/10 p-4">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold text-cyan-300">
              {target.liters}L
            </span>
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">
              ≈ {target.glasses} glasses (250 ml)
            </span>
          </div>
          <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
            Based on roughly 33 ml per kg — a general lifestyle reference, not a
            medical target. Needs vary with heat, activity and diet.
          </p>
        </div>
      )}
      <SmartwatchCTA variant="track" source="calc-hydration" />
    </CardShell>
  );
};
